import { useState, useRef, useEffect } from "react";
import { downloadXlsx } from "./xlsxExport";

/* Reusable Export-to-Excel button with a Summary / Detail dropdown.
 *
 * Props:
 *  - filenameBase: string — module name, used for the .xlsx filename
 *  - buildSummary: () => rows[]  (one row per record — exec view)
 *  - buildDetail:  () => rows[]  (multiple rows per record — line/fee/credit/transfer expansion)
 *  - lang: "en" | "es"
 *  - selectedCount: optional — number of selected records, surfaced in
 *      the menu so the user knows whether they are exporting selection
 *      vs the full filtered list.
 *  - totalCount: optional — number of visible (filtered) records, same
 *      purpose as selectedCount.
 *  - style: optional — wrapper style overrides
 *
 * Caller decides what records to include — the convention adopted in
 * GFI Nexus is: if selectedCount > 0, export the selection; otherwise
 * export the full filtered list. The button itself stays visible at
 * all times (per CLAUDE.md export-button decision).
 */

var T = {
  en: { btn: "Export Excel", summary: "Summary Export", detail: "Detail Export", summaryDesc: "One row per record", detailDesc: "Lines, fees, credits, transfers", selected: "selected", visible: "visible", empty: "Nothing to export." },
  es: { btn: "Exportar Excel", summary: "Resumen", detail: "Detallado", summaryDesc: "Un renglón por registro", detailDesc: "Líneas, fees, créditos, transferencias", selected: "seleccionados", visible: "visibles", empty: "Nada que exportar." },
};

export default function ExportButton(props) {
  var lang = props.lang || "es";
  var t = T[lang] || T.es;
  var [open, setOpen] = useState(false);
  var rootRef = useRef(null);

  useEffect(function () {
    if (!open) return;
    function onDocDown(e) { if (rootRef.current && !rootRef.current.contains(e.target) && !(e.target.closest && e.target.closest('[data-searchselect]'))) setOpen(false); }
    document.addEventListener("mousedown", onDocDown);
    return function () { document.removeEventListener("mousedown", onDocDown); };
  }, [open]);

  function doExport(mode) {
    setOpen(false);
    var rows;
    try {
      rows = mode === "summary" ? (props.buildSummary && props.buildSummary()) : (props.buildDetail && props.buildDetail());
    } catch (err) {
      try { console.error("Export build failed:", err); } catch (e2) {}
      window.alert(t.empty);
      return;
    }
    if (!rows || rows.length === 0) { window.alert(t.empty); return; }
    var modeLabel = mode === "summary" ? "Summary" : "Detail";
    var sheets = {};
    sheets[modeLabel] = rows;
    downloadXlsx(props.filenameBase || "Export", modeLabel, sheets);
  }

  /* 2026-10-20, Arturo (Bills + Shipments kebab menus): "export
     excel quedó en color verde con marco verde, dale el mismo
     formato que los demás elementos de esa misma ventana" — the
     button itself was always this hardcoded green pill, regardless
     of where it's embedded; `style` (above) only ever reached the
     outer wrapper div, never this button. New `btnStyle` prop lets a
     caller fully replace it (e.g. to blend into a Brushed Steel
     kebab menu's plain list items) — every other existing caller,
     which never passes it, keeps the exact same green button as
     before. */
  var btnStyle = {
    padding: "7px 14px",
    borderRadius: 6,
    border: "1px solid var(--color-accent)",
    background: "rgba(42,122,111,0.08)",
    color: "var(--color-accent)",
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    ...(props.btnStyle || {}),
  };
  /* Hard-coded #fff (not var(--color-bg-card)) so the menu is fully
     opaque on every theme — Mist/Noir/Teal all use semi-transparent
     card backgrounds that let table rows bleed through. Stronger
     shadow + 1.5px border give the menu enough lift to read against
     the brightest summary cards. */
  var menuStyle = {
    position: "absolute",
    top: "100%",
    right: 0,
    marginTop: 4,
    minWidth: 240,
    background: "#ffffff",
    border: "1.5px solid #d0d5dd",
    borderRadius: 8,
    boxShadow: "0 12px 32px rgba(0,0,0,0.22), 0 2px 6px rgba(0,0,0,0.08)",
    zIndex: 1000,
    overflow: "hidden",
  };
  var itemStyle = {
    display: "block", width: "100%", textAlign: "left",
    padding: "10px 14px",
    background: "#ffffff", border: "none", cursor: "pointer",
    fontSize: 12, color: "#1a1a1a",
    borderBottom: "1px solid #e5e7eb",
  };
  var itemHintStyle = { fontSize: 10, color: "#6b7280", marginTop: 2 };

  var sel = props.selectedCount || 0;
  var vis = props.totalCount || 0;
  var hint = sel > 0
    ? sel + " " + t.selected
    : (vis > 0 ? vis + " " + t.visible : "");

  return (
    <div ref={rootRef} style={{ position: "relative", display: "inline-block", ...(props.style || {}) }}>
      <button onClick={function () { setOpen(!open); }} style={btnStyle} title={hint}>
        <span style={{ fontSize: 13 }}>{"⬇"}</span>
        {t.btn}
        <span style={{ fontSize: 10, opacity: 0.7 }}>{"▾"}</span>
      </button>
      {open && (
        <div style={menuStyle}>
          <button onClick={function () { doExport("summary"); }}
            onMouseEnter={function (e) { e.currentTarget.style.background = "#f3f4f6"; }}
            onMouseLeave={function (e) { e.currentTarget.style.background = "#ffffff"; }}
            style={itemStyle}>
            <div style={{ fontWeight: 700 }}>{t.summary}</div>
            <div style={itemHintStyle}>{t.summaryDesc}{hint ? " · " + hint : ""}</div>
          </button>
          <button onClick={function () { doExport("detail"); }}
            onMouseEnter={function (e) { e.currentTarget.style.background = "#f3f4f6"; }}
            onMouseLeave={function (e) { e.currentTarget.style.background = "#ffffff"; }}
            style={{ ...itemStyle, borderBottom: "none" }}>
            <div style={{ fontWeight: 700 }}>{t.detail}</div>
            <div style={itemHintStyle}>{t.detailDesc}{hint ? " · " + hint : ""}</div>
          </button>
        </div>
      )}
    </div>
  );
}
